import React, { useEffect } from 'react';
import { useLocation, useParams, Link } from 'react-router-dom';
import Footer from './sections/Footer';
import { Helmet } from 'react-helmet-async';
import { IoIosColorPalette } from "react-icons/io";
import { DiResponsive } from "react-icons/di";
import { GiDistraction } from "react-icons/gi";
import projectsData from '../data/projectsData';
import TrackedLink from './global/TrackedLink';
import { CTA_IDS, CTA_LOCATIONS } from '../lib/analytics.constants';

// Model badge colors
const modelColors = {
  'Financiado': 'bg-blue-100 text-blue-700 border-blue-200',
  'Revenue Share': 'bg-purple-100 text-purple-700 border-purple-200',
  'Built for Equity': 'bg-amber-100 text-amber-700 border-amber-200',
};

function ProjectDetails({ footerRef }) {
  const { projectId } = useParams();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  const project = projectsData.find((p) => p.id === projectId);
  if (!project) {
    return (
      <div className="py-20 text-center">
        <h1 className="text-4xl font-bold text-[#0b2b40]">Proyecto no encontrado</h1>
        <p className="text-gray-600 mt-4">Por favor verificá la URL e intentá de nuevo.</p>
        <Link to="/" className="mt-6 inline-block text-green-600 hover:underline">
          Volver al inicio
        </Link>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{project.name} · Caso de estudio | eien</title>
        <meta name="description" content={`Caso de estudio de ${project.name}: ${project.description || 'desarrollo de software y producto digital por eien.'}`} />
        <link rel="canonical" href={`https://eientech.io/project-details/${project.id}`} />
      </Helmet>

      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header with Model Badge */}
          <div className="text-center mb-8">
            {project.modeloAplicado && (
              <span className={`inline-block px-4 py-1.5 rounded-full text-sm font-semibold border ${modelColors[project.modeloAplicado] || 'bg-gray-100 text-gray-700'} mb-4`}>
                Modelo: {project.modeloAplicado}
              </span>
            )}
            <h1 className="text-4xl font-bold mb-4 animate-fadeIn text-[#0b2b40]">
              {project.name}
            </h1>
          </div>

          {/* Quick Info Cards */}
          {(project.stack || project.timeline || project.impacto) && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {/* Stack */}
              {project.stack && (
                <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                  <h3 className="font-bold mb-3 flex items-center gap-2 text-[#0b2b40]">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                    </svg>
                    Stack Tecnológico
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {project.stack.map((tech, i) => (
                      <span key={i} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Timeline */}
              {project.timeline && (
                <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                  <h3 className="font-bold mb-3 flex items-center gap-2 text-[#0b2b40]">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Timeline
                  </h3>
                  <p className="text-2xl font-bold text-gray-900">{project.timeline}</p>
                  <p className="text-sm text-gray-500">de desarrollo</p>
                </div>
              )}

              {/* Impact */}
              {project.impacto && (
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-100">
                  <h3 className="font-bold mb-3 flex items-center gap-2 text-[#0b2b40]">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                    Impacto
                  </h3>
                  <div className="space-y-2">
                    {project.impacto.map((item, i) => (
                      <div key={i} className="flex items-baseline gap-2">
                        <span className="text-2xl font-bold text-green-600">{item.metric}</span>
                        <span className="text-sm text-gray-600">{item.description}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Introduction */}
          {project.introduction && (
            <div className="mb-12 bg-gray-100 p-8 rounded-xl shadow-md animate-slideUp">
              <h2 className="text-3xl font-semibold mb-6 text-center text-[#0b2b40]">
                Introducción del Proyecto
              </h2>
              <p className="text-gray-700 text-lg text-center max-w-3xl mx-auto">
                {project.introduction}
              </p>
            </div>
          )}

          {/* Team & Deliverables */}
          {(project.equipo || project.entregables) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              {project.equipo && (
                <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                  <h3 className="font-bold mb-4 flex items-center gap-2 text-[#0b2b40]">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Equipo asignado
                  </h3>
                  <ul className="space-y-2">
                    {project.equipo.map((member, i) => (
                      <li key={i} className="flex items-center gap-2 text-gray-700">
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        {member}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {project.entregables && (
                <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                  <h3 className="font-bold mb-4 flex items-center gap-2 text-[#0b2b40]">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                    Entregables
                  </h3>
                  <ul className="space-y-2">
                    {project.entregables.map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-gray-700">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Preview Image */}
          {project.preview && (
            <div className="mb-12 relative overflow-hidden rounded-xl shadow-lg">
              <img
                src={project.preview}
                alt={`Vista previa de ${project.name}`}
                className="w-full h-full object-cover transition-transform duration-500 hover:scale-105 animate-fadeIn"
                loading='lazy'
              />
            </div>
          )}

          {/* Project Details */}
          <div className="space-y-16">
            {project.details.map((detail, index) => (
              <div
                key={index}
                className={`grid grid-cols-1 lg:grid-cols-2 items-center gap-8 ${index % 2 === 0 ? "" : "lg:grid-cols-2-reverse"
                  }`}
              >
                {detail.image && (
                  <div className="relative overflow-hidden rounded-xl shadow-lg animate-slideLeft">
                    <img
                      src={detail.image}
                      alt={`Detalle de ${detail.title}`}
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                      loading='lazy'
                    />
                  </div>
                )}
                <div className="flex flex-col justify-center animate-slideRight">
                  <h3 className="text-2xl font-bold mb-4 text-[#0b2b40]">{detail.title}</h3>
                  <p className="text-gray-700 text-lg">{detail.content}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Design Highlights */}
      <div>
        <div className="mt-16 bg-gray-50 py-12 px-6 rounded-xl shadow-md">
          <h2 className="text-3xl font-semibold text-center mb-8 text-[#0b2b40]">¿Por qué elegimos este diseño?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-center">
            <div className="flex flex-col items-center text-center">
              <IoIosColorPalette size={50} color='#CD5C5C' />
              <h3 className="text-xl font-bold mb-2 text-[#0b2b40]">Colores Corporativos</h3>
              <p className="text-gray-600">
                Usamos los colores corporativos para reforzar la identidad de marca y generar confianza en los usuarios.
              </p>
            </div>
            <div className="flex flex-col items-center text-center">
              <DiResponsive size={100} color='#a6ca24' />
              <h3 className="text-xl font-bold mb-2 text-[#0b2b40]">Diseño Responsivo</h3>
              <p className="text-gray-600">
                Garantizamos que el sitio sea completamente accesible desde cualquier dispositivo, mejorando la experiencia del usuario.
              </p>
            </div>
            <div className="flex flex-col items-center text-center">
              <GiDistraction size={50} color='#247ad0' />
              <h3 className="text-xl font-bold mb-2 text-[#0b2b40]">Call-to-Action Destacados</h3>
              <p className="text-gray-600">
                Creamos botones llamativos para guiar a los usuarios hacia acciones clave, como explorar catálogos.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-4xl mx-auto mt-16 px-4">
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-3xl p-8 md:p-12 border border-green-100 text-center">
          <h3 className="text-2xl font-bold mb-4 text-[#0b2b40]">
            ¿Querés resultados similares?
          </h3>
          <p className="text-gray-600 mb-6 max-w-xl mx-auto">
            Contanos tu idea, te armamos roadmap, costo y plan de pagos en 48 horas.
          </p>
          <TrackedLink to="/agendar" ctaId={CTA_IDS.PROJECT_DETAILS_AGENDAR} ctaLocation={CTA_LOCATIONS.PAGE_CTA}>
            <button className="inline-flex items-center gap-2 bg-green-500 text-white px-8 py-4 rounded-xl font-semibold hover:bg-green-600 transition-colors duration-300 shadow-lg hover:shadow-xl">
              Armar mi plan
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
          </TrackedLink>
        </div>
      </div>

      <section className="mt-24" id="footer" ref={footerRef}>
        <Footer />
      </section>
    </>
  );
}

export default ProjectDetails;
